/*
 * GLSL for the 3D startup loader (WebGL2 / GLSL ES 3.00).
 *
 * SURFACE: studio-lit metal. Hemispheric ambient (navy sky, teal bounce), one
 * key light with Blinn specular, a procedural soft-box reflection band for the
 * metal sheen, and a teal fresnel rim that gives every part a glowing
 * silhouette. Per-draw effects:
 *   uReveal - dissolve-in; the dissolve front glows teal ("materialise").
 *   uGrow   - clips by the vertex sweep parameter, so tubes route themselves.
 *   uScanX  - a light plane sweeping the axis during the inspection act.
 * Cut faces of cutaways (data.y = 1) are lit as teal section caps.
 *
 * EDGE: GL_LINES, additive, fading with uEdge so outlines draw in before the
 * surface does. Surfaces use polygon offset so coplanar edges win.
 */

export const SURFACE_VS = `#version 300 es
layout(location = 0) in vec3 aPos;
layout(location = 1) in vec3 aNrm;
layout(location = 2) in vec2 aData;
uniform mat4 uViewProj;
uniform mat4 uModel;
out vec3 vWorld;
out vec3 vNrm;
out vec3 vLocal;
out vec2 vData;
void main() {
  vec4 w = uModel * vec4(aPos, 1.0);
  vWorld = w.xyz;
  vNrm = mat3(uModel) * aNrm;
  vLocal = aPos;
  vData = aData;
  gl_Position = uViewProj * w;
}`;

export const SURFACE_FS = `#version 300 es
precision highp float;
in vec3 vWorld;
in vec3 vNrm;
in vec3 vLocal;
in vec2 vData;
uniform vec3 uEye;
uniform vec3 uKeyDir;
uniform vec3 uSky;
uniform vec3 uGround;
uniform vec3 uEdgeCol;
uniform vec3 uBase;
uniform vec3 uEmissive;
uniform float uSpec;
uniform float uGloss;
uniform float uMetal;
uniform float uRim;
uniform float uReveal;
uniform float uGrow;
uniform float uScanX;
uniform float uScan;
out vec4 outColor;

float hash(vec3 p) {
  p = fract(p * 0.3183099 + 0.1);
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float noise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(hash(i), hash(i + vec3(1, 0, 0)), f.x), mix(hash(i + vec3(0, 1, 0)), hash(i + vec3(1, 1, 0)), f.x), f.y),
             mix(mix(hash(i + vec3(0, 0, 1)), hash(i + vec3(1, 0, 1)), f.x), mix(hash(i + vec3(0, 1, 1)), hash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
}

void main() {
  if (vData.x > uGrow + 0.0001) discard;
  float band = 0.0;
  if (uReveal < 0.999) {
    float n = noise(vLocal * 9.0) * 0.7 + noise(vLocal * 27.0) * 0.3;
    float th = uReveal * 1.25 - 0.1;
    if (n > th) discard;
    band = 1.0 - smoothstep(0.0, 0.07, th - n);
  }
  vec3 V = normalize(uEye - vWorld);
  vec3 N = normalize(vNrm);
  if (dot(N, V) < 0.0) N = -N;
  vec3 L = normalize(uKeyDir);
  float ndl = max(dot(N, L), 0.0);
  vec3 H = normalize(L + V);
  float spec = pow(max(dot(N, H), 0.0), uGloss) * uSpec;
  vec3 amb = mix(uGround, uSky, N.y * 0.5 + 0.5);
  vec3 R = reflect(-V, N);
  float box = smoothstep(0.05, 0.3, R.y) * (1.0 - smoothstep(0.55, 0.9, R.y));
  float strip = smoothstep(0.6, 0.95, abs(R.z)) * 0.5;
  vec3 env = mix(uGround, vec3(0.85, 0.92, 0.95), clamp(box + strip, 0.0, 1.0));
  vec3 diffuse = uBase * (amb + ndl * 0.85) * (1.0 - uMetal * 0.55);
  vec3 refl = env * mix(vec3(1.0), uBase, uMetal) * uMetal * 0.75;
  vec3 col = diffuse + refl + mix(vec3(1.0), uBase, uMetal * 0.6) * spec;
  float fres = pow(1.0 - max(dot(N, V), 0.0), 3.0);
  col += uEdgeCol * fres * uRim;
  col += uEmissive;
  if (vData.y > 0.5) col = mix(col, uEdgeCol * 0.5 + uBase * 0.15, 0.75);
  col += uEdgeCol * band * 2.2;
  float scan = exp(-pow((vWorld.x - uScanX) * 7.0, 2.0)) * uScan;
  col += uEdgeCol * scan * 0.9;
  col = col / (1.0 + col * 0.25);
  outColor = vec4(pow(col, vec3(1.0 / 2.2)), 1.0);
}`;

export const EDGE_VS = `#version 300 es
layout(location = 0) in vec4 aEdge;
uniform mat4 uViewProj;
uniform mat4 uModel;
out float vU;
out float vX;
void main() {
  vec4 w = uModel * vec4(aEdge.xyz, 1.0);
  vU = aEdge.w;
  vX = w.x;
  gl_Position = uViewProj * w;
}`;

export const EDGE_FS = `#version 300 es
precision highp float;
in float vU;
in float vX;
uniform vec3 uEdgeCol;
uniform float uEdge;
uniform float uGrow;
uniform float uScanX;
uniform float uScan;
out vec4 outColor;
void main() {
  if (vU > uGrow + 0.0001) discard;
  float scan = exp(-pow((vX - uScanX) * 7.0, 2.0)) * uScan;
  outColor = vec4(uEdgeCol * (uEdge * 0.42 + scan * 0.9), 1.0);
}`;
